import { Contract, InvoiceItem, PageDto } from '@ekt-group/general-purpose-api-interfaces';
import { ContractsSideloading } from '@ekt-group/general-purpose-api-interfaces/src/modules/contracts/types/contracts.enum';
import { CustomerContractRole } from '@ekt-group/general-purpose-api-interfaces/src/types/customer-contract-role.enum';
import { CalendarCheck02, Hash02, SearchMd, Settings04, Wallet03 } from '@untitled-ui/icons-react';
import { Badge, DatePicker, Empty, Table } from 'antd';
import { RangePickerProps } from 'antd/es/date-picker';
import dayjs, { Dayjs } from 'dayjs';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import { DATE_FORMAT } from '@/constants';
import { useAbility, useUser } from '@/features/auth';
import { useGetInvoices } from '@/features/invoices/api/use-get-invoices';
import { useGetContractsSaldo } from '@/features/invoices/api/use-get-saldo';
import ContractPaymentModal from '@/features/invoices/components/ContractPaymentModal';
import { useInvoicesTable } from '@/features/invoices/hooks';
import { getAllowedContractIdsFromPermissions } from '@/helpers/get-allowed-contract-ids-from-permissions';
import { calculateSaldo } from '@/helpers/invoices-saldo';
import { formatAsCurrency } from '@/helpers/number-formatter';
import { useBreakpoint } from '@/hooks/use-breakpoints';
import { useFilterParams } from '@/hooks/use-filter-params';
import { usePagination } from '@/hooks/use-pagination';
import { useRowExpand } from '@/hooks/use-row-expand';
import { useUserInfoStore } from '@/store';
import { cn } from '@/utils/cn';

import { useGetContracts } from '../../contracts/api/use-get-contracts';

const { RangePicker } = DatePicker;

type DateRange = [Dayjs | null, Dayjs | null] | null;

const InvoicesPage = () => {
  const { t } = useTranslation('invoicesPage');

  const { selectedAccount } = useUserInfoStore();
  const { data: user } = useUser();

  const [isPayAllModalOpen, setIsPayAllModalOpen] = useState(false);
  const [invoiceNoLike, setInvoiceNoLike] = useState<string>();
  const [isFiltersVisible, setIsFiltersVisible] = useState(false);

  const tableRef = useRef<HTMLDivElement | null>(null);
  const [columnWidths, setColumnWidths] = useState<number[]>([]);

  const { filters, setFilterValue, setFilterValues, clearAllFilters, hasFilters } = useFilterParams([
    'startDate',
    'endDate',
    'contractNumber',
    'invoiceNumber',
  ]);

  useEffect(() => {
    function updateWidths() {
      if (tableRef.current) {
        const thead = tableRef.current.querySelector('.ant-table-thead');
        if (thead) {
          const ths = thead.querySelectorAll<HTMLTableCellElement>('th');
          const widths = Array.from(ths).map((th) => Math.round(th.offsetWidth));
          setColumnWidths(widths);
        }
      }
    }

    const timeoutId = setTimeout(() => {
      updateWidths();
    }, 0);

    window.addEventListener('resize', updateWidths);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', updateWidths);
    };
  }, []);

  const [dateRange, setDateRange] = useState<DateRange>([
    filters.startDate ? dayjs(filters.startDate) : null,
    filters.endDate ? dayjs(filters.endDate) : null,
  ]);

  const contractsQuery = useGetContracts({
    params: {
      disablePagination: true,
      ownerCodes: [selectedAccount?.code],
      sideloading: [ContractsSideloading.ContractDebt],
    },
    queryConfig: {
      enabled: !!selectedAccount?.code,
    },
  });
  const contracts = contractsQuery.data as Contract[];

  const contractsSaldoQuery = useGetContractsSaldo({
    params: {
      contractNumbers: filters.contractNumber ? [filters.contractNumber] : contracts?.map(({ contractNumber }) => contractNumber),
    },
    queryConfig: {
      enabled: !!contracts?.length,
    },
  });

  const { page, pageSize, setPageSize, setPage } = usePagination();

  const invoicesQuery = useGetInvoices({
    params: {
      contractIds:
        user?.roles?.includes(CustomerContractRole.ChildContract) || user?.roles?.includes(CustomerContractRole.SharedObjectContract)
          ? getAllowedContractIdsFromPermissions(selectedAccount?.permissions)
          : undefined,
      ownerCodes: [selectedAccount?.code],
      contractNumbers: filters.contractNumber ? [filters.contractNumber] : null,
      page,
      pageSize,
      invoiceNoLike,
      ...(dateRange?.[0] && { startDate: dateRange[0].toISOString() }),
      ...(dateRange?.[1] && { endDate: dateRange[1].toISOString() }),
    },
    queryConfig: {
      enabled: !!selectedAccount?.code,
    },
  });
  const { handleExpand, expandedRowKeys } = useRowExpand({ allowMultiple: false });
  const { columns, expandedRowRender } = useInvoicesTable({ expandedRowKeys, handleExpand, columnWidths });
  const { isAboveMd } = useBreakpoint('md');

  const handleSearchByInvoiceNumber = () => {
    setInvoiceNoLike(filters.invoiceNumber);
  };

  const ability = useAbility();

  const navigate = useNavigate();

  useEffect(() => {
    if (!ability.can('read', 'contract')) {
      navigate('/');
    }
  }, [ability, navigate]);

  if (contractsQuery.isLoading || contractsQuery.isError) {
    return null;
  }

  const toggleFiltersVisibility = () => {
    setIsFiltersVisible(!isFiltersVisible);
  };

  const handlePayAllButtonClick = () => {
    setIsPayAllModalOpen(true);
  };

  const handlePayAllModalClose = () => {
    setIsPayAllModalOpen(false);
  };

  const handleDateRangeChange: RangePickerProps['onChange'] = (dates) => {
    setDateRange(dates);
    setFilterValues({
      startDate: dates?.[0] ? dates[0].toISOString() : null,
      endDate: dates?.[1] ? dates[1].toISOString() : null,
    });
  };

  const handleClearAllFilters = () => {
    setDateRange([null, null]);
    clearAllFilters();
  };

  const appliedFiltersCount = Object.values(filters)?.filter(Boolean).length;

  return (
    <>
      <div className="lg:px-8 md:px-6 w-full flex flex-col gap-5 h-full">
        <div className="bg-white p-6 rounded-[32px]">
          <div className="flex flex-col gap-2">
            <div className="grid grid-cols-10 gap-6">
              <div className="col-span-10 md:col-span-4 flex lg:flex-none md:flex-auto lg:justify-start items-center gap-2">
                <span className="font-bold text-[32px]">{t('invoices')}</span>
                <button onClick={toggleFiltersVisibility} className="flex items-center gap-1 py-[6px] px-2 text-[#A7AD2A]">
                  <Settings04 className="w-4 h-4" />
                  <span className="text-base font-medium">{t(isFiltersVisible ? 'closeFilters' : 'openFilters', { ns: 'common' })}</span>
                  {hasFilters && <Badge count={appliedFiltersCount} showZero={false} color="#A7AD2A" />}
                </button>
              </div>
              <div className="col-span-10 md:col-span-6 flex items-end sm:items-start justify-between md:justify-end gap-5">
                <div className="flex flex-col sm:flex-row gap-1 sm:gap-3 md:justify-end justify-start items-start sm:items-center">
                  <span className="text-bold text-[#8C8C8C] md:text-gray-10 text-sm sm:text-base md:text-[20px]">
                    {t('subjectToPayment')}
                  </span>
                  <span className="text-bold flex flex-1 items-center text-[24px] sm:text-[36px]">
                    {formatAsCurrency(contractsSaldoQuery.data?.data ? calculateSaldo(contractsSaldoQuery.data?.data)?.saldo : 0)}
                  </span>
                  {Boolean(contractsSaldoQuery.data?.data && calculateSaldo(contractsSaldoQuery.data?.data)?.penalty) && (
                    <span className="flex text-[#CF1322] py-1/2 sm:py-1 px-3 bg-[#FEE6E7] rounded-[99px] items-center justify-center text-center overflow-hidden text-base text-ellipsis whitespace-nowrap">
                      {t('penalty')}:{' '}
                      {formatAsCurrency(contractsSaldoQuery.data?.data ? calculateSaldo(contractsSaldoQuery.data?.data)?.penalty : 0)}
                    </span>
                  )}
                </div>
                <div className="flex justify-end items-center">
                  <Button onClick={handlePayAllButtonClick} className="flex-shrink-0 justify-center max-h-12 h-12 w-[180px]">
                    <Wallet03 />
                    <span className="w-auto font-medium text-base text-ellipsis whitespace-nowrap overflow-hidden">
                      {t('payAllInvoices')}
                    </span>
                  </Button>
                </div>
              </div>
            </div>
            {isFiltersVisible && (
              <div className="grid grid-cols-10 md:pt-0 pt-5 gap-2">
                <div className="col-span-10 md:col-span-3">
                  <Input
                    name="invoiceNumber"
                    value={filters.invoiceNumber}
                    onChange={(event) => setFilterValue('invoiceNumber', event.target.value)}
                    placeholder={t('invoiceNumber')}
                    parentClassName="h-12"
                    className="flex-1"
                    rightSide={
                      <button className="outline-none" onClick={handleSearchByInvoiceNumber}>
                        <SearchMd className="text-brand-brown w-6 h-6" />
                      </button>
                    }
                  />
                </div>
                <div className="col-span-10 md:col-span-3">
                  <Select
                    name="contractNumber"
                    value={filters.contractNumber}
                    onItemSelect={(value) => setFilterValue('contractNumber', value)}
                    className="h-12 flex-1"
                    placeholder={t('contractNumber')}
                    options={contracts?.map((el) => ({
                      value: el.contractNumber,
                      label: el.contractNumber,
                    }))}
                    leftSide={
                      <span className="w-6 h-6 text-brand-brown">
                        <Hash02 />
                      </span>
                    }
                  />
                </div>
                <div className="col-span-10 md:col-span-4 flex gap-2 flex-col sm:flex-row">
                  <RangePicker
                    format={DATE_FORMAT}
                    placeholder={[t('periodStart'), t('periodEnd')]}
                    suffixIcon={<CalendarCheck02 className="text-brand-brown w-6 h-6" />}
                    value={dateRange}
                    onChange={handleDateRangeChange}
                    className={cn(
                      'w-full border bg-gray-100 h-12 flex justify-between items-center px-4 gap-2 cursor-pointer rounded-lg',
                      dateRange && (dateRange[0] || dateRange[1]) ? 'border-gray-300 bg-white' : 'border-gray-100',
                    )}
                  />
                  <button
                    onClick={handleClearAllFilters}
                    className={cn(
                      'h-12 rounded-[8px] text-center sm:w-[120px] w-full justify-self-end items-center justify-center',
                      hasFilters ? 'bg-brand-green text-gray-10' : 'bg-[#F5F5F5] text-[#B9B9B9]',
                    )}
                  >
                    {t('clearAll')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        <div ref={tableRef} className="flex flex-col bg-white rounded-[32px] w-full h-full flex-1">
          <Table
            onRow={(record) => ({
              onClick: (event) => handleExpand(record.invoiceNo, event, !isAboveMd),
            })}
            rowClassName={(record) =>
              cn('!h-[65px] cursor-pointer', {
                'bg-[#F9FAE0]': expandedRowKeys.includes(record.invoiceNo) && !isAboveMd,
              })
            }
            rowKey={(record) => record.invoiceNo}
            className="w-full rounded-[32px]"
            loading={invoicesQuery.isLoading}
            locale={{
              emptyText: <Empty description={t('noData', { ns: 'table' })} />,
            }}
            expandable={{
              expandedRowClassName: () => cn('bg-[#F9FAE0] !mx-0 !px-0 [&_.ant-table-cell]:!p-0', { hidden: isAboveMd }),
              expandedRowKeys,
              expandedRowRender,
              rowExpandable: () => true,
              showExpandColumn: false,
            }}
            scroll={{ x: 'max-content' }}
            columns={columns}
            dataSource={(invoicesQuery.data as PageDto<InvoiceItem>)?.data || []}
            pagination={{
              defaultCurrent: page,
              hideOnSinglePage: true,
              pageSizeOptions: [10, 20, 50, 100],
              total: (invoicesQuery.data as PageDto<InvoiceItem>)?.meta?.totalItems,
              position: ['bottomRight'],
              onChange: setPage,
              onShowSizeChange: (_, size) => setPageSize(size),
              defaultPageSize: pageSize,
            }}
          />
        </div>
      </div>
      <ContractPaymentModal isOpen={isPayAllModalOpen} onClose={handlePayAllModalClose} />
    </>
  );
};

export default InvoicesPage;
